// import packages
import React, { useState, useCallback, useContext, useEffect } from "react";
import { useMutation } from "@apollo/client";

import {
  LegacyCard as Card,
  Text,
  Layout,
  FormLayout,
  Link,
  PageActions,
  Select,
  InlineStack,
  BlockStack,
  Toast,
} from "@shopify/polaris";

// import hoc
import { withErrorBoundary } from "lib/hoc";

// import query
import { CUSTOMIZE_FONT } from "app/setup/apollo";

import { baseHelper } from "lib/helpers";
// import components
import { Banner } from "lib/components";

// import context
import { PrivateContext } from "lib/context";

// import constant
import constant from "lib/constant/constant";
import { useNavigate } from "react-router-dom";

const CustomizeFont = () => {
  const { currentUser, cms } = useContext(PrivateContext);
  const navigate = useNavigate();
  const { updatedAt } = currentUser;
  const { DEFAULT_FONT, gql } = constant;
  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [font, setFont] = useState("");
  const [active, setActive] = useState(false);

  const [submitEnabled, setSubmitEnable] = useState(false);

  const [customizeFont, { loading: customizeLoading }] = useMutation(CUSTOMIZE_FONT);

  useEffect(() => {
    if (currentUser.font) {
      setFont(currentUser.font);
    }
  }, [currentUser]);

  const handleSubmit = async (value) => {
    const defaultFont = value || font;
    const res = await customizeFont({
      variables: { input: { font: defaultFont } },
    });
    const responseError = baseHelper.getResponseError(res.data, gql.CUSTOMIZE_FONT);
    const responseData = baseHelper.getResponseData(res.data, gql.CUSTOMIZE_FONT);

    if (responseData) {
      if (updatedAt) {
        setSuccessMessage(cms("success.message.fontUpdate"));
      } else {
        setSuccessMessage(cms("success.message.font"));
      }
      setSubmitEnable(false);
      setActive(true);
    }

    if (responseError) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: responseError,
      });
    }
    setTimeout(() => {
      navigate(-1); // we are directly manupulating the dom.
    }, 3000);
  };

  const onCloseBanner = () => {
    setBanner({
      isOpen: false,
      status: "",
      title: "",
    });
  };

  const handleReset = (val) => {
    setFont(val);
    setSubmitEnable(true);
  };

  const handleChange = (value) => {
    setFont(value);
    setSubmitEnable(true);
  };

  const toggleActive = useCallback(() => setActive((activeToast) => !activeToast), []);

  const valueOptions = [
    {
      label: DEFAULT_FONT,
      disabled: true,
      value: DEFAULT_FONT,
    },
    {
      label: "Arial (Sans-Serif)",
      value: "Arial",
    },
    {
      label: "Brush Script MT (Cursive)",
      value: "Brush Script MT",
    },
    {
      label: "Courier New (Monospace)",
      value: "Monospace",
    },
    {
      label: "Garamond (Serif)",
      value: "Garamond",
    },
    {
      label: "Georgia(Serif)",
      value: "Georgia",
    },
    {
      label: "Helvetica(San - Serif)",
      value: "Helvetica",
    },
    {
      label: "Tahoma(San - Serif)",
      value: "Tahoma",
    },
    {
      label: "Times New Roman(Serif)",
      value: "Times New Roman Cyr",
    },
    {
      label: "Trebuchet MS(San - Serif)",
      value: "Trebuchet MS",
    },
    {
      label: "Verdana(San - Serif)",
      value: "Verdana",
    },
  ];

  return (
    <>
      {banner.isOpen && (
        <Layout.Section>
          <Banner isOpen={banner.isOpen} status={banner.status} title={banner.title} onDismiss={onCloseBanner} />
        </Layout.Section>
      )}
      <Layout.AnnotatedSection title={cms("label.section.title")} description={cms("label.section.description")}>
        {active && <Toast content={successMessage} onDismiss={toggleActive} />}
        <Card
          title={[
            cms("label.cardSection.title"),
            updatedAt && (
              <Text as="span" tone="subdued">
                <Text variant="bodySm" as="p">{`${baseHelper.lastUpdateDate(updatedAt)}`}</Text>
              </Text>
            ),
          ]}
        >
          <Card.Section>
            <BlockStack gap="2">{cms("label.cardSection.description")}</BlockStack>
            <FormLayout>
              <InlineStack />
              <div className="default-font">
                <Select
                  id="standardTypeSelect"
                  options={valueOptions}
                  value={font}
                  onChange={(selectedValue) => handleChange(selectedValue)}
                />
              </div>
              <InlineStack />
              <InlineStack align="space-between">
                <span style={{ fontFamily: font }}>{cms("label.cardSection.sample")}</span>
                {font !== DEFAULT_FONT && (
                  <Link onClick={() => handleReset(DEFAULT_FONT)}>{cms("label.cardSection.reset")}</Link>
                )}
              </InlineStack>
            </FormLayout>
          </Card.Section>
        </Card>
        {!updatedAt && (
          <PageActions
            primaryAction={{
              content: `${cms("common.button.submit")}`,
              onAction: () => handleSubmit(),
              loading: customizeLoading,
              disabled: !submitEnabled,
            }}
            secondaryActions={[
              {
                content: cms("common.button.cancel"),
                onAction: () => navigate("/setting"),
              },
            ]}
          />
        )}
        {updatedAt && (
          <PageActions
            primaryAction={{
              content: `${cms("common.button.update")}`,
              onAction: () => handleSubmit(),
              loading: customizeLoading,
              disabled: !submitEnabled,
            }}
            secondaryActions={[
              {
                content: cms("common.button.cancel"),
                onAction: () => navigate("/setting"),
              },
            ]}
          />
        )}
      </Layout.AnnotatedSection>
      <Layout.Section />
    </>
  );
};

export default withErrorBoundary(CustomizeFont);
